import React from "react";
import { Link } from "react-router-dom";

const PrivacyPage = () => {
  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <Link to="/" style={styles.logo}>striply</Link>
        <nav style={styles.nav}>
          <Link to="/editor" style={styles.navLink}>Editor</Link>
          <Link to="/about" style={styles.navLink}>About</Link>
        </nav>
      </header>

      <main style={styles.main}>
        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.updated}>Last updated: February 2025</p>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Overview</h2>
          <p style={styles.text}>
            Striply is a comic layout design tool. We are committed to protecting
            your privacy and being transparent about what data we collect and how
            we use it.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>What We Collect</h2>
          <p style={styles.text}>
            <strong>Layout Data:</strong> When you use the editor, your panel layouts
            and configurations are stored to provide the service. This includes panel
            positions, sizes, and layout settings.
          </p>
          <p style={styles.text}>
            <strong>Images:</strong> Images you upload for panel backgrounds are
            processed in your browser and are not permanently stored on our servers.
            They exist only during your session.
          </p>
          <p style={styles.text}>
            <strong>Usage Analytics:</strong> We may collect anonymous usage data
            to improve the product, such as which features are used and general
            usage patterns. This data is not tied to your identity.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Cookies and Advertising</h2>
          <p style={styles.text}>
            We use cookies for essential site functionality. We also use Google
            AdSense to display advertisements, which may use cookies and web
            beacons to serve ads based on your prior visits to this or other
            websites. Google's use of advertising cookies enables it and its
            partners to serve ads based on your browsing activity.
          </p>
          <p style={styles.text}>
            You may opt out of personalized advertising by visiting{" "}
            <a href="https://www.google.com/settings/ads" style={styles.link}>
              Google Ads Settings
            </a>
            . For more information about how Google uses data when you use our
            site, visit{" "}
            <a href="https://policies.google.com/technologies/partner-sites" style={styles.link}>
              Google's Privacy & Terms
            </a>
            .
          </p>
          <p style={styles.text}>
            You can also control cookie preferences through your browser settings.
            Disabling cookies may affect some site functionality.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Third-Party Services</h2>
          <p style={styles.text}>
            We use the following third-party services:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Google AdSense</strong> — for displaying advertisements
              (<a href="https://policies.google.com/privacy" style={styles.link}>Google Privacy Policy</a>)
            </li>
            <li style={styles.listItem}>
              <strong>Google Analytics</strong> — for understanding usage patterns
              (<a href="https://support.google.com/analytics/answer/6004245" style={styles.link}>Analytics Privacy</a>)
            </li>
            <li style={styles.listItem}>
              <strong>Fly.io</strong> — for website hosting and infrastructure
            </li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Data Security</h2>
          <p style={styles.text}>
            We implement reasonable security measures to protect your data. However,
            no internet transmission is completely secure. Use the service at your
            own discretion.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Your Rights</h2>
          <p style={styles.text}>
            You have the right to:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Access your data</li>
            <li style={styles.listItem}>Request deletion of your data</li>
            <li style={styles.listItem}>Opt out of analytics and advertising cookies</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Changes to This Policy</h2>
          <p style={styles.text}>
            We may update this privacy policy from time to time. Significant changes
            will be noted on this page with an updated revision date.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Contact</h2>
          <p style={styles.text}>
            If you have questions about this privacy policy, please contact us
            at <a href="mailto:contact@striply.xyz" style={styles.link}>contact@striply.xyz</a>.
          </p>
        </section>
      </main>

      <footer style={styles.footer}>
        <nav style={styles.footerNav}>
          <Link to="/" style={styles.footerLink}>Home</Link>
          <Link to="/editor" style={styles.footerLink}>Editor</Link>
          <Link to="/about" style={styles.footerLink}>About</Link>
          <Link to="/terms" style={styles.footerLink}>Terms</Link>
        </nav>
      </footer>
    </div>
  );
};

const styles = {
  container: {
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#1a1a1a",
    lineHeight: 1.7,
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "#f5f0e8",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 40px",
    borderBottom: "2px solid #1a1a1a",
  },
  logo: {
    fontSize: 28,
    fontWeight: 700,
    color: "#1a1a1a",
    textDecoration: "none",
    fontFamily: "'Georgia', serif",
  },
  nav: {
    display: "flex",
    gap: 20,
  },
  navLink: {
    color: "#555",
    textDecoration: "none",
    fontSize: 15,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  main: {
    flex: 1,
    maxWidth: 720,
    margin: "0 auto",
    padding: "48px 24px",
  },
  title: {
    fontSize: 42,
    fontWeight: 700,
    marginBottom: 8,
    color: "#1a1a1a",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  updated: {
    fontSize: 14,
    color: "#888",
    marginBottom: 40,
    fontStyle: "italic",
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 12,
    color: "#1a1a1a",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  text: {
    fontSize: 15,
    marginBottom: 12,
    color: "#444",
  },
  list: {
    paddingLeft: 24,
    marginBottom: 12,
  },
  listItem: {
    fontSize: 15,
    marginBottom: 8,
    color: "#444",
  },
  link: {
    color: "#1a73e8",
    textDecoration: "underline",
  },
  footer: {
    padding: "24px 40px",
    borderTop: "2px solid #1a1a1a",
    background: "#f5f0e8",
  },
  footerNav: {
    display: "flex",
    justifyContent: "center",
    gap: 32,
  },
  footerLink: {
    color: "#666",
    textDecoration: "none",
    fontSize: 14,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
};

export default PrivacyPage;
